import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackBarComponent } from '../../snackbar/snackbar.component';
import { SpinnerService } from '../../../services/spinner/spinner.service';
import { InviteAutomationState, TeamsService } from '../../../services/teams/teams.service';

export type InviteStatus = 'Accepted' | 'Invited' | 'Uninvited';

export type SendInvitesInvitee = {
  memberId: string;
  firstName: string;
  lastName: string;
  department?: string;
  location?: string;
  division?: string;
  state?: string;
  payType?: string;
  employmentType?: string;
  filterValues?: Record<string, string>;
  email: string;
  inviteSentAt?: string | Date | null;
  inviteAcceptedAt?: string | Date | null;
  dayOfLogin?: string | Date | null;
};

export type SendInvitesDialogData = {
  teamId: string;
  teamName: string;
  invitees: SendInvitesInvitee[];
  availableFilters?: Array<[string, string[]]>;
};

export type SendInvitesDialogResult = { invitesUpdated: boolean };

type StatusFilter = 'All Statuses' | InviteStatus;
type ActiveFilterChip = { kind: 'payroll' | 'status'; key: string; label: string; value: string };

@Component({
    selector: 'app-send-invites-dialog',
    templateUrl: './send-invites-dialog.component.html',
    styleUrls: ['./send-invites-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SendInvitesDialogComponent implements OnInit {
  search = '';
  activeFiltersMap: Record<string, string[]> = {};
  activeFilterChips: ActiveFilterChip[] = [];
  statusFilter: StatusFilter = 'All Statuses';
  selected = new Set<string>(); // Contains team member IDs.
  isSending = false;
  automation: InviteAutomationState | null = null;
  automationDate: Date | null = null;
  isAutomationSaving = false;
  readonly minAutomationDate = new Date();
  private invitesUpdated = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SendInvitesDialogData,
    private ref: MatDialogRef<SendInvitesDialogComponent, SendInvitesDialogResult>,
    private teamsService: TeamsService,
    private spinnerService: SpinnerService,
    private snackBar: MatSnackBar,
  ) {
    this.initializeSelectedInvitees();
  }

  async ngOnInit(): Promise<void> {
    await this.loadInviteAutomation();
  }

  close(): void {
    this.ref.close({ invitesUpdated: this.invitesUpdated });
  }

  get invitees(): SendInvitesInvitee[] {
    return this.data.invitees ?? [];
  }

  get availablePayrollFilters(): Array<[string, string[]]> {
    return this.data.availableFilters ?? [];
  }

  get filteredInvitees(): SendInvitesInvitee[] {
    const q = (this.search ?? '').trim().toLowerCase();

    return this.invitees.filter((i) => {
      const matchesSearch =
        !q ||
        `${i.firstName} ${i.lastName}`.toLowerCase().includes(q) ||
        (i.email ?? '').toLowerCase().includes(q);

      const matchesFilters = this.matchesActiveFilters(i);
      const normalizedStatus = this.getInviteStatus(i);
      const matchesStatus =
        this.statusFilter === 'All Statuses' || normalizedStatus === this.statusFilter;

      return matchesSearch && matchesFilters && matchesStatus;
    });
  }

  trackPayrollFilter(index: number, filter: [string, string[]]): string {
    return filter?.[0] || `${index}`;
  }

  trackActiveFilterChip(index: number, filter: ActiveFilterChip): string {
    return `${filter.kind}:${filter.key}:${filter.value}:${index}`;
  }

  onStatusFilterChange(value: StatusFilter): void {
    this.statusFilter = value;
    this.refreshActiveFilterChips();
  }

  private refreshActiveFilterChips(): void {
    const chips: ActiveFilterChip[] = [];
    for (const [key, values] of Object.entries(this.activeFiltersMap || {})) {
      for (const value of values || []) {
        chips.push({ kind: 'payroll', key, label: key, value });
      }
    }
    if (this.statusFilter !== 'All Statuses') {
      chips.push({ kind: 'status', key: 'status', label: 'Status', value: this.statusFilter });
    }
    this.activeFilterChips = chips.sort((a, b) => `${a.label}: ${a.value}`.localeCompare(`${b.label}: ${b.value}`));
  }

  activeFilterCount(filterKey: string): number {
    const values = this.activeFiltersMap?.[filterKey];
    return Array.isArray(values) ? values.length : 0;
  }

  displayFilterValue(filterKey: string, value: string): string {
    const rawValue = `${value ?? ''}`.trim();
    if (!rawValue) {
      return '';
    }

    const normalizedFilterKey = this.normalizeFilterKey(filterKey);
    if (!['location', 'department', 'division'].includes(normalizedFilterKey)) {
      return rawValue;
    }

    const escapedFilterKey = `${filterKey || ''}`.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (!escapedFilterKey) {
      return rawValue;
    }

    return rawValue.replace(new RegExp(`^${escapedFilterKey}\\s*\\/\\s*`, 'i'), '').trim();
  }

  get allStatuses(): InviteStatus[] {
    return ['Accepted', 'Invited', 'Uninvited'];
  }

  get selectedCount(): number {
    return this.invitees.filter((i) => this.selected.has(i.memberId)).length;
  }

  get automationSummary(): string {
    if (!this.automation) {
      return 'Automated invite sending status is unavailable.';
    }
    if (!this.automation.enabled) {
      return 'Automated invite sending is off.';
    }
    if (this.automation.initialInviteSendAt && !this.automation.initialInviteSentAt) {
      return `Initial send on ${this.formatDate(this.automation.initialInviteSendAt)} for ${this.automation.pendingInitialCount || 0} not invited.`;
    }
    if (this.automation.nextRetryAt) {
      return `Retry on ${this.formatDate(this.automation.nextRetryAt)} for ${this.automation.pendingRetryCount || 0} pending signup.`;
    }
    return 'Automated invite sending is on.';
  }

  get maxAutomationDate(): Date | null {
    return this.automation ? this.buildMaxAutomationDate(this.automation.initialInviteWaitDays) : null;
  }

  isSelected(memberId: string): boolean {
    return this.selected.has(memberId);
  }

  toggleOne(memberId: string, checked: boolean): void {
    if (checked) {
      this.selected.add(memberId);
    } else {
      this.selected.delete(memberId);
    }
  }

  handleFilterSelect(filterKey: string, filterValue: string | string[]): void {
    const values = Array.isArray(filterValue) ? filterValue : [filterValue];
    const normalizedValues = values.map((value) => `${value || ''}`.trim()).filter((value) => !!value);
    if (!normalizedValues.length) {
      delete this.activeFiltersMap[filterKey];
      this.refreshActiveFilterChips();
      return;
    }
    this.activeFiltersMap[filterKey] = normalizedValues;
    this.refreshActiveFilterChips();
  }

  clearFilter(filterKey: string, filterValue: string): void {
    const currentValues = this.activeFiltersMap[filterKey] || [];
    const nextValues = currentValues.filter((value) => value !== filterValue);
    if (!nextValues.length) {
      delete this.activeFiltersMap[filterKey];
      this.refreshActiveFilterChips();
      return;
    }
    this.activeFiltersMap[filterKey] = nextValues;
    this.refreshActiveFilterChips();
  }

  removeActiveFilter(filter: ActiveFilterChip): void {
    if (filter.kind === 'status') {
      this.statusFilter = 'All Statuses';
      this.refreshActiveFilterChips();
      return;
    }

    this.clearFilter(filter.key, filter.value);
  }

  clearAllFilters(): void {
    this.activeFiltersMap = {};
    this.statusFilter = 'All Statuses';
    this.refreshActiveFilterChips();
  }

  toggleAllVisible(checked: boolean): void {
    const selectableVisibleInvitees = this.filteredInvitees.filter((i) => this.isInviteeSelectable(i));
    if (checked) {
      for (const i of selectableVisibleInvitees) {
        this.selected.add(i.memberId);
      }
    } else {
      for (const i of selectableVisibleInvitees) {
        this.selected.delete(i.memberId);
      }
    }
  }

  get allVisibleSelected(): boolean {
    const visible = this.filteredInvitees.filter((i) => this.isInviteeSelectable(i));
    if (!visible.length) {
      return false;
    }
    return visible.every((i) => this.selected.has(i.memberId));
  }

  get someVisibleSelected(): boolean {
    const visible = this.filteredInvitees.filter((i) => this.isInviteeSelectable(i));
    if (!visible.length) {
      return false;
    }
    const any = visible.some((i) => this.selected.has(i.memberId));
    const all = visible.every((i) => this.selected.has(i.memberId));
    return any && !all;
  }

  private initializeSelectedInvitees(): void {
    this.selected = new Set(
      this.invitees
        .filter((invitee) => this.isInviteeSelectable(invitee) && this.getInviteStatus(invitee) === 'Uninvited')
        .map((invitee) => invitee.memberId),
    );
  }

  async sendInviteNow(): Promise<void> {
    const selectedInvitees = this.invitees.filter(
      (invitee) => this.selected.has(invitee.memberId) && this.isInviteeSelectable(invitee),
    );
    if (!selectedInvitees.length) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Error: Select at least one employee before sending emails.',
      });
      return;
    }

    this.isSending = true;
    this.spinnerService.show('Sending emails...');
    try {
      const sent: SendInvitesInvitee[] = [];
      const failed: SendInvitesInvitee[] = [];
      for (const invitee of selectedInvitees) {
        try {
          await this.teamsService.resendEmailToTeamMember(invitee.memberId, this.data.teamId);
          sent.push(invitee);
        } catch {
          failed.push(invitee);
        }
      }

      const now = new Date().toISOString();
      for (const invitee of sent) {
        invitee.inviteSentAt = now;
        this.selected.delete(invitee.memberId);
      }

      this.invitesUpdated = this.invitesUpdated || sent.length > 0;
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: this.mountMessage(sent.length, failed.map((invitee) => invitee.email)),
      });
    } catch (error: any) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: `Error sending emails: ${error.message || error.toString()}`,
      });
    } finally {
      this.spinnerService.hide();
      this.isSending = false;
    }
  }

  async scheduleAutomation(): Promise<void> {
    if (!this.automationDate) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Error: Select a send date.',
      });
      return;
    }
    if (!this.automation) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Error: Automated invite config is unavailable.',
      });
      return;
    }

    this.isAutomationSaving = true;
    try {
      const response = await this.teamsService.updateInviteAutomation(this.data.teamId, {
        enabled: true,
        initialInviteSendAt: this.automationDate.toISOString(),
      });
      if (!response.success) {
        throw new Error(`${response.error || response.message || 'Unable to schedule invites.'}`);
      }
      this.automation = response.data;
      this.invitesUpdated = true;
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: `Invites scheduled for ${this.formatDate(response.data.initialInviteSendAt)}.`,
      });
    } catch (error: any) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: `Error scheduling invites: ${error.message || error.toString()}`,
      });
    } finally {
      this.isAutomationSaving = false;
    }
  }

  async disableAutomation(): Promise<void> {
    this.isAutomationSaving = true;
    try {
      const response = await this.teamsService.updateInviteAutomation(this.data.teamId, {
        enabled: false,
      });
      if (!response.success) {
        throw new Error(`${response.error || response.message || 'Unable to disable invites.'}`);
      }
      this.automation = response.data;
      this.invitesUpdated = true;
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Automated invite sending disabled.',
      });
    } catch (error: any) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: `Error disabling invites: ${error.message || error.toString()}`,
      });
    } finally {
      this.isAutomationSaving = false;
    }
  }

  getInviteeName(i: SendInvitesInvitee): string {
    return `${i.firstName} ${i.lastName}`.trim();
  }

  getInviteeDepartmentDisplay(i: SendInvitesInvitee): string {
    return this.displayFilterValue('Department', i.department || '') || '—';
  }

  getInviteeLocationDisplay(i: SendInvitesInvitee): string {
    return this.displayFilterValue('Location', i.location || '') || '—';
  }

  getInviteStatus(i: SendInvitesInvitee): InviteStatus {
    if (i.inviteAcceptedAt) {
      return 'Accepted';
    }
    if (i.inviteSentAt) {
      return 'Invited';
    }
    return 'Uninvited';
  }

  getInviteStatusPillClass(status: InviteStatus): string {
    if (status === 'Accepted') {
      return 'pill pill-green';
    }
    if (status === 'Invited') {
      return 'pill pill-yellow';
    }
    return 'pill pill-muted';
  }

  isInviteeSelectable(invitee: SendInvitesInvitee): boolean {
    return !!invitee?.memberId;
  }

  private matchesActiveFilters(invitee: SendInvitesInvitee): boolean {
    const activeFilters = Object.entries(this.activeFiltersMap || {});
    if (!activeFilters.length) {
      return true;
    }

    for (const [filterKey, values] of activeFilters) {
      const inviteeValue = this.getInviteeFilterValue(invitee, filterKey);
      if (!inviteeValue || !values.includes(inviteeValue)) {
        return false;
      }
    }
    return true;
  }

  private async loadInviteAutomation(): Promise<void> {
    try {
      const response = await this.teamsService.getInviteAutomation(this.data.teamId);
      if (response.success) {
        this.automation = response.data;
      }
    } catch {
      this.automation = null;
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Unable to load automated invite status.',
      });
    }
  }

  private buildMaxAutomationDate(days: number): Date {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date;
  }

  private formatDate(value: string | Date | null): string {
    if (!value) {
      return '';
    }
    return new Date(value).toLocaleDateString();
  }

  private getInviteeFilterValue(invitee: SendInvitesInvitee, filterKey: string): string {
    const directValue = `${invitee?.filterValues?.[filterKey] ?? ''}`.trim();
    if (directValue) {
      return directValue;
    }

    const normalizedFilterKey = this.normalizeFilterKey(filterKey);
    for (const [existingFilterKey, value] of Object.entries(invitee?.filterValues || {})) {
      if (this.normalizeFilterKey(existingFilterKey) === normalizedFilterKey) {
        const normalizedValue = `${value || ''}`.trim();
        if (normalizedValue) {
          return normalizedValue;
        }
      }
    }

    if (normalizedFilterKey === 'department') {
      return `${invitee.department || ''}`.trim();
    }
    if (normalizedFilterKey === 'location') {
      return `${invitee.location || ''}`.trim();
    }
    if (normalizedFilterKey === 'division') {
      return `${invitee.division || ''}`.trim();
    }
    if (normalizedFilterKey === 'state') {
      return `${invitee.state || ''}`.trim();
    }
    if (normalizedFilterKey === 'paytype') {
      return `${invitee.payType || ''}`.trim();
    }
    if (normalizedFilterKey === 'employmenttype' || normalizedFilterKey === 'employeetype') {
      return `${invitee.employmentType || ''}`.trim();
    }

    return '';
  }

  private normalizeFilterKey(filterKey: string): string {
    return `${filterKey || ''}`.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private mountMessage(sentCount: number, failedEmails: string[]): string {
    if (sentCount === 0 && failedEmails.length === 0) {
      return 'No emails were sent.';
    }
    let message = '';
    if (sentCount > 0) {
      message += `${sentCount} email${sentCount === 1 ? '' : 's'} sent.`;
    }
    if (failedEmails.length > 0) {
      if (message) {
        message += ' ';
      }
      message += `Failed: ${failedEmails.join(', ')}.`;
    }
    return message;
  }
}
